import React, {useEffect, useRef, useState} from "react";
import {
    deleteApproverPreset,
    getApproverPresets,
    getApproverPresetById,
    saveApproverPreset,
} from "../../../../../api/approval/ApproverPresetService";
import {approvalProcessToApprovers, approversToPresetSaveRequest} from "../approvalUtils";
import {handleActiveError, handleActiveToast} from "../../../../../components/sonner/ToastBasic";

function SettingIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 2.375C3 1.615 3.616 1 4.375 1h1.75c.76 0 1.375.616 1.375 1.375v4.25C7.5 7.385 6.884 8 6.125 8h-1.75C3.615 8 3 7.384 3 6.625V5.25H0v-1.5h3zm1.5.125v4H6v-4zM16 5.25H8.5v-1.5H16zM8.5 9.375C8.5 8.615 9.116 8 9.875 8h1.75C12.385 8 13 8.616 13 9.375v1.375h3v1.5h-3v1.375c0 .76-.616 1.375-1.375 1.375h-1.75c-.76 0-1.375-.616-1.375-1.375zM10 9.5v4h1.5v-4zM0 12.25v-1.5h7.5v1.5z"
            />
        </svg>
    );
}

export default function ApprovePreset({values, onSelectPreset}) {
    const [isOpen, setIsOpen] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [presetName, setPresetName] = useState("");
    const [presetDescription, setPresetDescription] = useState("");
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applyingId, setApplyingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const dropdownRef = useRef(null);

    const loadPresets = async () => {
        setLoading(true);
        try {
            const data = await getApproverPresets();
            setPresets(Array.isArray(data) ? data : []);
        } catch {
            handleActiveError({message: "프리셋 목록을 불러오지 못했습니다."});
            setPresets([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        loadPresets();
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setShowSaveDialog(false);
            }
        };

        if (isOpen || showSaveDialog) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, showSaveDialog]);

    const handleSaveClick = () => {
        if (!values?.length) {
            handleActiveToast({message: "먼저 결재자를 지정해주세요."});
            return;
        }
        setShowSaveDialog(true);
        setIsOpen(false);
    };

    const handleSavePreset = async () => {
        if (!presetName.trim()) {
            handleActiveToast({message: "프리셋 이름을 입력해주세요."});
            return;
        }
        if (!values?.length) {
            handleActiveToast({message: "저장할 결재자가 없습니다."});
            return;
        }

        setSaving(true);
        try {
            const requestDto = approversToPresetSaveRequest({
                name: presetName,
                description: presetDescription,
                approvers: values,
            });

            if (!requestDto.users.length) {
                handleActiveToast({message: "저장할 결재자가 없습니다."});
                return;
            }

            await saveApproverPreset(requestDto);
            handleActiveToast({message: "프리셋이 저장되었습니다."});
            setShowSaveDialog(false);
            setPresetName("");
            setPresetDescription("");
            await loadPresets();
        } catch (error) {
            const message = error?.response?.data?.message
                ?? error?.response?.data?.title
                ?? "프리셋 저장에 실패했습니다.";
            handleActiveError({message});
        } finally {
            setSaving(false);
        }
    };

    const handleSelectPreset = async (preset) => {
        if (!preset?.id) {
            handleActiveError({message: "유효하지 않은 프리셋입니다."});
            return;
        }

        setApplyingId(preset.id);
        try {
            const process = await getApproverPresetById(preset.id);
            const approvers = approvalProcessToApprovers(process);

            if (!approvers.length) {
                handleActiveToast({message: "프리셋에 결재자가 없습니다."});
                return;
            }

            onSelectPreset?.(approvers);
            setIsOpen(false);
        } catch {
            handleActiveError({message: "프리셋 결재라인을 불러오지 못했습니다."});
        } finally {
            setApplyingId(null);
        }
    };

    const handleDeletePreset = async (e, presetId) => {
        e.stopPropagation();
        if (!window.confirm("정말 이 프리셋을 삭제하시겠습니까?")) return;

        try {
            await deleteApproverPreset(presetId);
            handleActiveToast({message: "프리셋이 삭제되었습니다."});
            await loadPresets();
        } catch {
            handleActiveError({message: "프리셋 삭제에 실패했습니다."});
        }
    };

    return (
        <div className="approve-modal__preset" ref={dropdownRef}>
            <button
                type="button"
                className={`approve-modal__preset-btn ${isOpen ? "is-open" : ""}`}
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <span className={`text-[12px]`}>프리셋</span>
                <SettingIcon />
            </button>

            {isOpen ? (
                <div className="approve-modal__preset-menu">
                    {loading ? (
                        <div className="approve-modal__preset-loading">불러오는 중…</div>
                    ) : presets.length > 0 ? (
                        presets.map((preset) => (
                            <div key={preset.id} className="approve-modal__preset-item">
                                <button
                                    type="button"
                                    className="approve-modal__preset-item-name"
                                    disabled={applyingId === preset.id}
                                    onClick={() => handleSelectPreset(preset)}
                                >
                                    <span>{preset.name}</span>
                                    {preset.description ? (
                                        <span className="approve-modal__preset-item-desc">
                                            {preset.description}
                                        </span>
                                    ) : null}
                                </button>
                                <button
                                    type="button"
                                    className="approve-modal__preset-item-delete"
                                    onClick={(e) => handleDeletePreset(e, preset.id)}
                                >
                                    삭제
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className="approve-modal__preset-empty">저장된 프리셋이 없습니다</div>
                    )}

                    <button
                        type="button"
                        className="approve-modal__preset-save"
                        onClick={handleSaveClick}
                    >
                        + 현재 설정 저장
                    </button>
                </div>
            ) : null}

            {showSaveDialog ? (
                <div className="approve-modal__preset-dialog">
                    <h4 className="approve-modal__preset-dialog-title">프리셋 저장</h4>
                    <input
                        type="text"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                        placeholder="프리셋 이름 입력"
                        className="approve-modal__preset-dialog-input"
                        autoFocus
                    />
                    <input
                        type="text"
                        value={presetDescription}
                        onChange={(e) => setPresetDescription(e.target.value)}
                        placeholder="설명 (선택)"
                        className="approve-modal__preset-dialog-input"
                    />
                    <div className="approve-modal__preset-dialog-actions">
                        <button
                            type="button"
                            className="approve-modal__preset-dialog-btn approve-modal__preset-dialog-btn--primary"
                            disabled={saving}
                            onClick={handleSavePreset}
                        >
                            {saving ? "저장 중…" : "저장"}
                        </button>
                        <button
                            type="button"
                            className="approve-modal__preset-dialog-btn"
                            onClick={() => {
                                setShowSaveDialog(false);
                                setPresetName("");
                                setPresetDescription("");
                            }}
                        >
                            취소
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
